/*======================================================================================================================
  * Bluebell: src/app/state/maps/map.effects.ts
  * Last Edited: 15/01/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import {
  createMap, createMapFailure, createMapSuccess,
  deleteMap, deleteMapFailure, deleteMapSuccess,
  retrieveMaps, retrieveMapsFailure, retrieveMapsSuccess,
  updateMap, updateMapFailure, updateMapSuccess,
} from '@state/maps/map.actions';
import { Map } from '@state/maps/map.interfaces';
import { concatMap, mergeMap, switchMap } from 'rxjs';


@Injectable()
export class MapEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private handler = inject(EffectHandlerService);


  create$ = createEffect(() => this.actions$.pipe(
    ofType(createMap),
    concatMap(({ mapPartial }) => this.api.createMap(mapPartial).pipe(
      this.handler.effectResponse({
        success: (map: Map) => createMapSuccess({ map }),
        retry: createMap({ mapPartial }),
        failure: createMapFailure(),
        failureMessage: 'Error Creating Map',
      })
    ))
  ));

  retrieve$ = createEffect(() => this.actions$.pipe(
    ofType(retrieveMaps),
    switchMap(() => this.api.listMaps().pipe(
      this.handler.effectResponse({
        success: (maps: Map[]) => retrieveMapsSuccess({ maps }),
        retry: retrieveMaps(),
        failure: retrieveMapsFailure(),
        failureMessage: 'Error Loading Maps'
      })
    ))
  ));

  update$ = createEffect(() => this.actions$.pipe(
    ofType(updateMap),
    mergeMap(({ map }) => this.api.updateMap(map).pipe(
      this.handler.effectResponse({
        success: (updatedMap: Map) => updateMapSuccess({ map: updatedMap }),
        successMessage: 'Map Updated Successfully',
        retry: updateMap({ map }),
        failure: updateMapFailure(),
        failureMessage: 'Error Updating Map'
      })
    ))
  ));


  delete$ = createEffect(() => this.actions$.pipe(
    ofType(deleteMap),
    mergeMap(({ uuid }) => this.api.deleteMap(uuid).pipe(
      this.handler.effectResponse({
        success: () => deleteMapSuccess({uuid}),
        retry: deleteMap({uuid}),
        failure: deleteMapFailure(),
        failureMessage: 'Error Deleting Map'
      })
    ))
  ));
}
